<template>
	<div
		class="pointer-events-none absolute top-0 right-0 bottom-10 left-0 z-8 flex flex-col items-center justify-center py-8 transition-colors duration-1000"
		:class="{
			'bg-black/0': (!show || noBg) && !keep,
			'bg-black': (show && !noBg) || keep,
			'overflow-hidden': !noBg
		}"
	>
		<RouletteRipple
			horizontal
			:color="randomColor"
			:customColor="customColor"
			animate
			prefix="avatar"
			v-if="animate && !noBg"
			class="transition-opacity duration-1000"
			:class="{ 'opacity-0': !show }"
		/>

		<div
			class="relative h-18 w-19.5 transition-opacity duration-1000"
			:class="{ 'opacity-0': !show }"
		>
			<div
				class="flex"
				:style="{ transform: `translateX(${translateX}px)` }"
				:class="{
					'transition-transform': show,
					'duration-2500 ease-out': !pullBack && show,
					'duration-500 ease-[cubic-bezier(0.4,_0.0,_0.3,_2)]': pullBack && show
				}"
				ref="roulette"
			>
				<div
					v-for="(item, index) in list"
					class="mx-0.75 rounded-full border-2 bg-black p-0.5"
					:class="[
						item[1],
						{
							'translate-y-[300%] opacity-0': !show && pullBack,
							'transition-all ease-in': pullBack,
							'duration-700': index === 30,
							'duration-600': index === 29 || index === 31,
							'duration-500': index === 28 || index === 32,
							'duration-400': index === 27 || index === 33,
							'duration-300': index === 26 || index === 34,
							'duration-200': index === 25 || index === 35,
							'duration-100': index <= 24 || index >= 36
						}
					]"
				>
					<LazyImage
						:src="item[0]"
						alt="avatar"
						class="h-16 w-16 rounded-full"
					/>
				</div>
			</div>
			<CaretPixelDown
				class="absolute -top-6 left-1/2 h-4.5 w-4.5 -translate-x-1/2 transition-transform"
				:class="{
					'!-translate-y-full': !show,
					'duration-1500': !pullBack,
					'translate-y-4 duration-300': pullBack
				}"
			/>
		</div>

		<RouletteExpensive
			v-if="animate && !noBg"
			class="transition-all duration-500"
			:class="{ 'scale-200 opacity-0': !show || keep }"
		/>

		<div
			class="absolute top-0 bottom-0 left-0 w-10 bg-gradient-to-r from-black/50 to-black/0 transition-opacity duration-1000"
			:class="{ 'opacity-0': !show }"
			v-if="!noBg"
		></div>
		<div
			class="absolute top-0 right-0 bottom-0 w-10 bg-gradient-to-l from-black/50 to-black/0 transition-opacity duration-1000"
			:class="{ 'opacity-0': !show }"
			v-if="!noBg"
		></div>
		<div
			class="absolute top-0 right-0 left-0 h-16 -translate-y-px transform bg-gradient-to-b from-black to-black/0 transition-opacity duration-1000"
			:class="{ 'opacity-0': !show }"
			v-if="!noBg"
		></div>
		<div
			class="absolute right-0 bottom-0 left-0 h-16 translate-y-px transform bg-gradient-to-t from-black to-black/0 transition-opacity duration-1000"
			:class="{ 'opacity-0': !show }"
			v-if="!noBg"
		></div>
	</div>
</template>
<script setup lang="ts">
import { nextTick, onUnmounted, ref } from 'vue'
import { sleep } from '@/utils/sleep'
import { avatarSm } from '@/utils/urls'
import RouletteRipple from './RouletteRipple.vue'
import LazyImage from './LazyImage.vue'
import RouletteExpensive from './RouletteExpensive.vue'
import CaretPixelDown from '@/assets/icons/caret-pixel-down.svg'
import { WebApp } from '@grammyjs/web-app'
import { playHapticSequence } from '@/utils/haptic'
import { useSeededRandom } from '@/composables/useSeededRandom'
import { chance } from '@/utils/chance'

const colors = [
	'border-[#E01533]', // red
	'border-[#E4A211]', // yellow
	'border-[#E06315]', // orange
	'border-[#84B611]' // green
]
const customColors = [
	'text-[#E01533]!', // red
	'text-[#E4A211]!', // yellow
	'text-[#E06315]!', // orange
	'text-[#84B611]!' // green
]

const props = defineProps<{
	seed: string
	noBg?: boolean
	isJackpot?: boolean
	isCrazy?: boolean
}>()

const show = ref(false)
const keep = ref(false)
const translateX = ref(1036)
const pullBack = ref(false)
const animate = ref(false)
const list = ref<[string, string][]>([])
const randomColor = ref<'blue' | 'orange' | 'pink' | 'red'>('blue')
const customColor = ref<string>()

const fillAvatars = (
	avatars: [string, number, number][],
	seededRandom: ReturnType<typeof useSeededRandom>
) => {
	const result: [string, string][] = []
	const tickets = avatars.map((a, i) => ({ teamIndex: i, tickets: a[2] }))

	for (let i = 0; i < 40; i++) {
		let real = seededRandom.next()
		let avatar = avatars.find((a, i) => {
			const chanceValue = chance(i, tickets, props.isCrazy)

			if (real < chanceValue) {
				return true
			}

			real -= chanceValue

			return false
		})

		if (!avatar) {
			avatar = avatars[0]
		}

		const [url, team] = avatar
		const urlSm = avatarSm(url)
		const color =
			props.isJackpot && colors[team] ? colors[team] : 'border-fill-secondary'

		result.push([urlSm, color])
	}

	list.value = result
}

let animationFrame = false
let haptics = false
let lastPassedIndex: number | undefined = undefined

const startHaptics = () => {
	WebApp.HapticFeedback.notificationOccurred('success')

	haptics = true
}

const stopHaptics = () => {
	haptics = false
	lastPassedIndex = undefined
}

const processHaptics = () => {
	if (!haptics) {
		return
	}

	const oneItem = 78
	const currentIndex = Math.floor(currentDrift / oneItem)

	if (lastPassedIndex !== currentIndex && currentIndex <= 0) {
		WebApp.HapticFeedback.selectionChanged()

		lastPassedIndex = currentIndex
	}
}

let currentDrift = 0
const roulette = ref()

const rotateCheck = () => {
	if (!animationFrame) {
		return
	}

	if (!roulette.value) {
		return
	}

	const transform = roulette.value.computedStyleMap().get('transform')

	if (!transform || !transform[0]) {
		return
	}

	const x = transform[0].x.value

	currentDrift = x
}

const startAnimationFrame = () => {
	animationFrame = true

	requestAnimationFrame(() => {
		const oldAngle = currentDrift
		rotateCheck()
		const newAngle = currentDrift
		if (oldAngle !== newAngle) {
			processHaptics()
		}

		if (animationFrame) {
			startAnimationFrame()
		}
	})
}

const stopAnimationFrame = () => {
	animationFrame = false
}

const reset = async (seededRandom: ReturnType<typeof useSeededRandom>) => {
	keep.value = false
	show.value = false
	animate.value = false
	list.value = []
	pullBack.value = false
	randomColor.value = ['blue', 'orange', 'pink', 'red'][
		Math.floor(seededRandom.next() * 4)
	] as 'blue' | 'orange' | 'pink' | 'red'

	await nextTick()

	translateX.value = 1036

	stopHaptics()
	startHaptics()
	startAnimationFrame()
}

const roll = async (
	avatars: [string, number, number][],
	avatar: [string, number]
) => {
	const seededRandom = useSeededRandom(props.seed)

	await reset(seededRandom)

	fillAvatars(avatars, seededRandom)

	const [url, team] = avatar
	const urlSm = avatarSm(url)
	const color =
		props.isJackpot && colors[team] ? colors[team] : 'border-fill-secondary'

	list.value[30] = [urlSm, color]

	if (props.isJackpot) {
		console.info('custom color', customColors[team])
		customColor.value = customColors[team]
	}

	await nextTick()

	show.value = true

	await nextTick()

	translateX.value = -78 * 30 - 30 + seededRandom.next() * 60

	await sleep(2500)

	pullBack.value = true

	translateX.value = -78 * 30

	await sleep(150)

	animate.value = true

	playHapticSequence(['error', 'error', 'error'])

	if (props.isJackpot) {
		await sleep(1500)

		keep.value = true

		await sleep(500)
	} else {
		await sleep(1000)
	}

	show.value = false

	stopAnimationFrame()
	stopHaptics()
}

onUnmounted(() => {
	stopAnimationFrame()
	stopHaptics()
})

defineExpose({
	roll
})
</script>
